import React, { useState, useContext, useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
    Button,
    Box,
    Paper,
    Typography,
    CircularProgress,
} from '@material-ui/core';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ImagesContext from "../contexts/ImagesContext";
import ImageUploader from "react-images-upload";
import { brand } from '../theme';

const useStyles = makeStyles((theme) => ({
    card: {
        padding: theme.spacing(3),
        border: `1px solid ${brand.line}`,
        marginBottom: theme.spacing(3),
        [theme.breakpoints.down('xs')]: {
            padding: theme.spacing(2),
        },
    },
    cardHeader: {
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: theme.spacing(2),
        marginBottom: theme.spacing(2.5),
    },
    muted: {
        color: theme.palette.text.secondary,
    },
    actions: {
        display: 'flex',
        justifyContent: 'flex-end',
        marginTop: theme.spacing(2),
        [theme.breakpoints.down('xs')]: {
            '& > *': { width: '100%' },
        },
    },
    grid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
        gap: theme.spacing(1.5),
        [theme.breakpoints.up('sm')]: {
            gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
        },
        [theme.breakpoints.up('md')]: {
            gridTemplateColumns: 'repeat(5, minmax(0, 1fr))',
        },
    },
    tile: {
        position: 'relative',
        padding: 0,
        border: '3px solid transparent',
        borderRadius: theme.shape.borderRadius,
        overflow: 'hidden',
        cursor: 'pointer',
        aspectRatio: '1 / 1',
        backgroundColor: '#EDEAE3',
        '&:focus-visible': {
            outline: `3px solid ${brand.accent}`,
            outlineOffset: 2,
        },
    },
    tileSelected: {
        borderColor: theme.palette.error.main,
        '& img': {
            opacity: 0.7,
        },
    },
    tileImage: {
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        display: 'block',
    },
    check: {
        position: 'absolute',
        top: 8,
        right: 8,
        width: 26,
        height: 26,
        borderRadius: '50%',
        backgroundColor: 'rgba(255,255,255,0.9)',
        border: `2px solid ${brand.line}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: theme.palette.error.main,
        '& svg': {
            fontSize: 28,
        },
    },
    checkSelected: {
        border: 0,
        backgroundColor: '#FFFFFF',
    },
    deleteButton: {
        color: theme.palette.error.main,
        borderColor: theme.palette.error.main,
        '&:hover': {
            backgroundColor: 'rgba(244,67,54,0.06)',
            borderColor: theme.palette.error.dark,
        },
    },
}));

export default function AdminImages() {
    const classes = useStyles();
    const [images, setImages] = useState([]);
    const imagesContext = useContext(ImagesContext);
    const { imageURLs, fetchAllImages, uploadImages, deleteImages } = imagesContext;
    const [imagesToUpload, setImagesToUpload] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [deleting, setDeleting] = useState(false);

    useEffect(() => {
        const imagesFromStorage = []
        imageURLs.forEach((downloadUrl) => {
            imagesFromStorage.push(
                {
                    src: downloadUrl,
                    thumbnail: downloadUrl,
                    isSelected: false
                }
            )
        });
        setImages(imagesFromStorage);
    }, [imageURLs]);

    const onSelectImage = (index) => {
        const copiedImages = [...images];
        copiedImages[index] = { ...copiedImages[index], isSelected: !copiedImages[index].isSelected };
        setImages(copiedImages);
    };

    const onDrop = picture => {
        setImagesToUpload([...imagesToUpload, picture]);
    };

    const onUploadImagesClicked = () => {
        if (imagesToUpload.length === 0) return;
        setUploading(true);
        uploadImages(imagesToUpload[imagesToUpload.length - 1])
            .then(() => {
                fetchAllImages();
            })
            .catch((error) => {
                console.error(error);
            })
            .finally(() => setUploading(false));
    };

    const onDeleteImagesClicked = () => {
        const selectedImages = []
        images.forEach((image) => {
            if (image.isSelected) {
                selectedImages.push(image);
            }
        });
        setDeleting(true);
        deleteImages(selectedImages)
            .then(() => {
                fetchAllImages();
            })
            .catch((error) => {
                console.error(error);
            })
            .finally(() => setDeleting(false));
    };

    const selectedCount = images.filter((image) => image.isSelected).length;

    return (
        <>
            <Paper className={classes.card}>
                <Box className={classes.cardHeader}>
                    <div>
                        <Typography variant="h5" component="h2">Upload photos</Typography>
                        <Typography variant="body2" className={classes.muted}>
                            Add project photos to the gallery on the homepage. JPG, GIF or PNG.
                        </Typography>
                    </div>
                </Box>
                <ImageUploader
                    withPreview={true}
                    withIcon={true}
                    onChange={onDrop}
                    imgExtension={[".jpg", ".jpeg", ".gif", ".png"]}
                />
                <Box className={classes.actions}>
                    <Button
                        onClick={onUploadImagesClicked}
                        variant="contained"
                        color="secondary"
                        disabled={uploading}
                        startIcon={uploading ? <CircularProgress size={18} color="inherit" /> : <CloudUploadIcon />}>
                        Upload images
                    </Button>
                </Box>
            </Paper>

            <Paper className={classes.card}>
                <Box className={classes.cardHeader}>
                    <div>
                        <Typography variant="h5" component="h2">Gallery photos</Typography>
                        <Typography variant="body2" className={classes.muted}>
                            {images.length === 0
                                ? 'No photos in the gallery yet.'
                                : `${images.length} photo${images.length === 1 ? '' : 's'}. Select photos to delete them.`}
                        </Typography>
                    </div>
                    {images.length > 0 && (
                        <Button
                            onClick={onDeleteImagesClicked}
                            variant="outlined"
                            disabled={selectedCount === 0 || deleting}
                            className={classes.deleteButton}
                            startIcon={deleting ? <CircularProgress size={18} color="inherit" /> : <DeleteOutlineIcon />}>
                            Delete selected{selectedCount > 0 ? ` (${selectedCount})` : ''}
                        </Button>
                    )}
                </Box>

                {images.length > 0 && (
                    <div className={classes.grid}>
                        {images.map((image, index) => (
                            <button
                                type="button"
                                key={image.src}
                                onClick={() => onSelectImage(index)}
                                aria-pressed={image.isSelected}
                                aria-label={`Photo ${index + 1}${image.isSelected ? ', selected' : ''}`}
                                className={`${classes.tile} ${image.isSelected ? classes.tileSelected : ''}`}>
                                <img src={image.thumbnail} alt="" loading="lazy" className={classes.tileImage} />
                                <span className={`${classes.check} ${image.isSelected ? classes.checkSelected : ''}`}>
                                    {image.isSelected && <CheckCircleIcon />}
                                </span>
                            </button>
                        ))}
                    </div>
                )}
            </Paper>
        </>
    );
}
